import React, {useState} from 'react';
import {useNavigate, useMatch, useLocation, NavLink, NavLinkProps} from 'react-router-dom';

const NavLinkBehavior = React.forwardRef<HTMLAnchorElement, NavLinkProps & { focusVisibleClassName?: string }>(
    function NavLinkBehavior({focusVisibleClassName, ...rest}, ref) {
        void focusVisibleClassName;
        return <NavLink ref={ref} {...rest} />;
    }
);
import useUser from '@/hooks/useUser';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import StorageIcon from '@mui/icons-material/Storage';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import {useThemeMode} from '@/styles/ThemeModeProvider';
import HeaderVersion from './HeaderVersion';
import {appConfig} from '@/config/appConfig';
import {getHeaderEnvironmentLabel, shouldShowGitLabLink} from '@/config/environment';
import {isAppMode} from '@/config/mockAuth';
// @ts-ignore — JS module
import Connector from '@/api/agent';
// @ts-ignore — JS module
import {PlatformRoot} from '@/routes';
import {getAppPlatforms} from '@/platformConfig';
// @ts-ignore — JS module
import usePlatform from '@/hooks/usePlatform';
import UserMenu from './UserMenu';
import DocsMenu from './DocsMenu';
import HeaderBreadcrumbs from './HeaderBreadcrumbs';
import {useTranslation} from 'react-i18next';
import {useUserRoles} from '@/hooks/User/useUserRoles';
import {useTutorial} from '@/context/TutorialContext';
import {getCurrentPageLabel, hasPageTutorialFor, toRelativePath} from '@/lib/tutorial/tutorialSteps';
import './Header.css';

interface HeaderProps {
    onMenuToggle?: () => void;
}

interface ReduxUser {
    authorities?: string[];
    allAuthorities?: string[];
}

interface HeaderAppState {
    init: {user: ReduxUser | null};
}

const Header: React.FC<HeaderProps> = ({onMenuToggle}) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {isDark, toggleMode} = useThemeMode();
    const environmentLabel = isAppMode() ? null : getHeaderEnvironmentLabel();
    const showGitLabLink = shouldShowGitLabLink();
    const user = useUser();
    const {activeRoles} = useUserRoles(user);

    const currentPlatform = usePlatform();
    const appPlatforms = getAppPlatforms();
    const platformMatch = useMatch(PlatformRoot.getRoute());
    const currentSubPath: string = platformMatch?.params['*'] ?? '';

    const {pathname} = useLocation();
    const {startFull, startPage} = useTutorial();
    const relativePath = toRelativePath(pathname, currentPlatform);
    const currentPageLabel = getCurrentPageLabel(t, relativePath);
    const showPageTutorial = !!currentPageLabel && hasPageTutorialFor(t, relativePath);

    const handleGitLabClick = () => {
        window.open(appConfig.links.gitlab, '_blank', 'noopener,noreferrer');
    };

    const handlePlatformChange = (_event: React.MouseEvent<HTMLElement>, newPlatform: string | null) => {
        if (newPlatform && newPlatform !== currentPlatform) {
            navigate(PlatformRoot.getLink({platform: newPlatform}) + currentSubPath);
        }
    };

    return (
        <AppBar position="fixed" className="header-appbar" data-tutorial="app-header">
            <Toolbar className="header-toolbar">
                {onMenuToggle && (
                    <IconButton
                        color="inherit"
                        onClick={onMenuToggle}
                        className="mobile-menu-button"
                        edge="start"
                        aria-label="Open navigation menu"
                        title="Open navigation menu"
                    >
                        <MenuIcon/>
                    </IconButton>
                )}

                <Box className="logo-container" onClick={() => navigate('/')}>
                    <img
                        style={{borderRadius: '5px'}}
                        src={appConfig.branding.logo.path}
                        alt={appConfig.branding.logo.alt}
                        height={appConfig.branding.logo.height}
                        className="app-logo"
                    />
                </Box>

                <Box className="header-title-stack">
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
                        <Typography variant="h6" component="div" className="header-title">
                            {isAppMode() ? 'REMUS App' : appConfig.app.name}
                        </Typography>
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{opacity: 0.7}}
                            className="header-version"
                        >
                            <HeaderVersion/>
                        </Typography>
                        {environmentLabel && (
                            <Typography
                                variant="body2"
                                component="span"
                                className="header-environment-label"
                            >
                                {environmentLabel.toUpperCase()}
                            </Typography>
                        )}
                    </Box>

                    <Box className="header-breadcrumbs-wrapper">
                        <HeaderBreadcrumbs/>
                    </Box>
                </Box>

                <Box sx={{flexGrow: 1}}/>

                <Box className="user-section">
                    {/* Platform switcher (PROD/TEST). Shown whenever more than one
                        platform is configured; the app build drives the list via
                        VITE_APP_PLATFORMS (see getAppPlatforms / .env.app). */}
                    {appPlatforms.length > 1 && (
                        <ToggleButtonGroup
                            value={currentPlatform}
                            exclusive
                            onChange={handlePlatformChange}
                            size="small"
                            className="platform-switcher"
                            aria-label="Platform"
                            data-tutorial="platform-switcher"
                        >
                            {appPlatforms.map((platform: string) => (
                                <ToggleButton
                                    key={platform}
                                    value={platform}
                                    component={NavLinkBehavior}
                                    to={PlatformRoot.getLink({platform}) + currentSubPath}
                                    className="platform-toggle"
                                >
                                    <StorageIcon fontSize="small" sx={{mr: 0.5}}/>
                                    {platform}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}

                    <DocsMenu
                        activeRoles={activeRoles}
                        pageLabel={currentPageLabel}
                        tutorial={{
                            onStartFull: startFull,
                            page: showPageTutorial ? {onStart: startPage} : undefined,
                        }}
                    />

                    <IconButton
                        color="inherit"
                        onClick={toggleMode}
                        className="theme-toggle-button"
                        aria-label={t('header.toggleTheme', isDark ? 'Switch to light theme' : 'Switch to dark theme')}
                        title={isDark ? t('header.lightMode', 'Light mode') : t('header.darkMode', 'Dark mode')}
                    >
                        {isDark ? <LightModeIcon fontSize="small"/> : <DarkModeIcon fontSize="small"/>}
                    </IconButton>

                    {showGitLabLink && (
                        <IconButton
                            color="inherit"
                            onClick={handleGitLabClick}
                            aria-label={t('header.gitlab')}
                            className="gitlab-button"
                            title="GitLab"
                        >
                            <img src="/gitlabicon.png" alt="GitLab" style={{width: 24, height: 24}}/>
                        </IconButton>
                    )}

                    <UserMenu/>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
